import { Button, Flex, Icon, Tooltip, useMediaQuery } from '@chakra-ui/react'
import customProtocolCheck from 'custom-protocol-check'
import { addHours } from 'date-fns'
import { useAtom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import styled from 'styled-components'
import iconCloseXGray100 from '~/assets/icons-light-theme/icon-close-x-gray100.svg'
import iconOpenLink from '~/assets/icons-light-theme/icon-openLink-dark900.svg?react'
import iconGear from '~/assets/svgs/icon-gear.svg?react'
import { StorageKeys } from '~/atoms/StorageKeys'
import { Text } from '~/components/core'
import { ImageIcon } from '~/components/ImageIcon'
import { MangoButtonVariant } from '~/components/shared/componentStyles/MangoButton.style'
import { MangoButton } from '~/components/shared/MangoButton'
import { light } from '~/constants/theme'
import urls from '~/constants/urls'
import { BannerProps } from '~/pages/Home/Banners/BannerManager'
import { ApplicationRoutes } from '~/types/RoutingTypes'
import { Size } from '~/types/ThemeTypes'
import { isElectron } from '~/utils/common'
import { openInNewTab } from '~/utils/functions'

interface WrapperProps {
  show: boolean
}

const Wrapper = styled.div<WrapperProps>`
  height: ${({ show }) => (show ? '56' : '0')}px;
  padding: 0 16px;
  display: flex;
  width: 100%;
  justify-content: space-between;
  background-color: ${light.green100};
  align-items: center;
  overflow: hidden;
  transition: all 200ms ease;
`
const TextWrapper = styled.div`
  display: flex;
  align-items: center;
`

interface DesktopAppDetectionBannerState {
  ignoreDesktopApp?: boolean
  hasDesktopApp?: boolean
  lastCheckedDate?: Date
}

export const desktopAppDetectionBannerStateAtom =
  atomWithStorage<DesktopAppDetectionBannerState>(
    StorageKeys.DesktopAppDetection,
    {},
  )

const DesktopAppDetectionBanner = ({ onShowCallback }: BannerProps) => {
  const [showBanner, setShowBanner] = useState(false)
  const [showDesktopDownload, setShowDesktopDownload] = useState(false)
  const location = useLocation()
  const [isSmall] = useMediaQuery(['(width < 860px)'])
  const navigate = useNavigate()

  const [desktopAppDetectionBannerState, setDesktopAppDetectionBannerState] =
    useAtom(desktopAppDetectionBannerStateAtom)

  const customProtocolCheckAttempt = () =>
    customProtocolCheck(
      `mango://${location.pathname === '/' ? '' : `${location.pathname.slice(1)}${location.search.length > 0 ? `${location.search}` : ''}`}`,
      () => {
        handleSetShow(true)
        setShowDesktopDownload(true)
      }, // Custom protocol is not supported
      () => handleSetShow(false), // Custom protocol is supported
      6000,
    )

  const shouldShowBanner = (): boolean => {
    if (isElectron) {
      return false
    }

    // If the user has explicitly said to ignore the desktop app, don't show it
    if (desktopAppDetectionBannerState.ignoreDesktopApp) {
      return false
    }

    // If the user has never checked for the desktop app, show it
    if (!desktopAppDetectionBannerState.lastCheckedDate) {
      return true
    }

    const lastCheckedDate = new Date(
      desktopAppDetectionBannerState.lastCheckedDate,
    )

    // If the user has indicated that they don't want to ignore the desktop app, or if dismissed the
    // banner more than 3 hours ago, show it
    return (
      desktopAppDetectionBannerState.ignoreDesktopApp === false ||
      addHours(lastCheckedDate, 3) < new Date()
    )
  }

  const updateLastCheckedDate = () => {
    setDesktopAppDetectionBannerState({
      ...desktopAppDetectionBannerState,
      lastCheckedDate: new Date(),
    })
  }

  const updateIgnoreDesktopApp = () => {
    setDesktopAppDetectionBannerState({
      ...desktopAppDetectionBannerState,
      ignoreDesktopApp: true,
      lastCheckedDate: new Date(),
    })
  }

  const handleSetShow = (val: boolean) => {
    onShowCallback(val)
    setShowBanner(val)
  }

  const handleIgnoreBanner = () => {
    handleSetShow(false)
    updateIgnoreDesktopApp()
  }

  const handleDismissBanner = () => {
    updateLastCheckedDate()
    handleSetShow(false)
  }

  const handleLaunchApp = () => {
    customProtocolCheckAttempt()
    handleSetShow(false)
  }

  const handleGotoDownload = () => {
    openInNewTab(urls.DESKTOP_APP_DOWNLOAD)
    handleSetShow(false)
  }

  const handleOpenSettings = () => {
    navigate(ApplicationRoutes.SETTINGS)
  }

  useEffect(() => {
    if (!showBanner && shouldShowBanner() && !isElectron) {
      handleSetShow(true)

      if (typeof customProtocolCheckAttempt === 'function') {
        customProtocolCheckAttempt()
      }
    }
  }, [isElectron])

  useEffect(() => {
    if (!showBanner) {
      return
    }

    onShowCallback(showBanner)
  }, [showBanner])

  return (
    <Wrapper show={showBanner}>
      <TextWrapper>
        {!isSmall && !showDesktopDownload && (
          <Text color={light.gray100}>
            Click <strong>Open Mango Voice</strong> to launch the app.
          </Text>
        )}
        {!isSmall && showDesktopDownload && (
          <Text color={light.gray100}>
            Click <strong>Try Mango</strong> to download the app.
          </Text>
        )}
        {!showDesktopDownload && (
          <Tooltip label={'Open the Mango Voice app, if installed'}>
            <Button
              onClick={handleLaunchApp}
              bg="gray.100"
              borderRadius="full"
              _hover={{ bg: 'green.100' }}
              size="sm"
              style={{ marginLeft: '.75rem', minWidth: 'auto' }}
            >
              Open Mango Voice
            </Button>
          </Tooltip>
        )}
        {showDesktopDownload && (
          <Tooltip label={'Download the Mango Voice app'}>
            <Button
              onClick={handleGotoDownload}
              bg="gray.100"
              borderRadius="full"
              _hover={{ bg: 'green.100' }}
              size="sm"
              style={{ marginLeft: '.75rem', minWidth: 'auto' }}
              rightIcon={<Icon fontSize={'.8rem'} as={iconOpenLink} />}
            >
              Try Mango
            </Button>
          </Tooltip>
        )}
      </TextWrapper>
      <Flex flexShrink={1} justifyContent="flex-end" alignItems={'center'}>
        <Tooltip label={'Settings'} placement={'bottom-start'}>
          <div style={{ cursor: 'pointer', marginLeft: '1rem' }}>
            <Icon
              style={{
                color: light.gray100,
              }}
              onClick={handleOpenSettings}
              width="20px"
              height="20px"
              as={iconGear}
            />
          </div>
        </Tooltip>
        <MangoButton
          style={{ marginLeft: '.5rem' }}
          size={Size.sm}
          variant={MangoButtonVariant.Destructive_200}
          onClick={handleIgnoreBanner}
        >
          Don't Ask Again
        </MangoButton>
        <Tooltip label={'Remind me later'} placement={'bottom-start'}>
          <div style={{ cursor: 'pointer', marginLeft: '1rem' }}>
            <ImageIcon
              onClick={handleDismissBanner}
              width="16"
              height="16"
              icon={iconCloseXGray100}
            />
          </div>
        </Tooltip>
      </Flex>
    </Wrapper>
  )
}

export default DesktopAppDetectionBanner
